/**
 * The TWO-ENDPOINT rule (`ai-docs/lexiun-user-setup.md` §2-bis) as pure state,
 * so every surface that renders an IM channel's progress answers the same way,
 * and the answer is unit-testable (lexide vitest runs in `environment: "node"`).
 *
 * Endpoint 1 is the assistant's own door (its bot / its WhatsApp link);
 * endpoint 2 is the lawyer's verified command door. A channel with only
 * endpoint 1 is "Etapa 1 de 2" — never connected, ready or active.
 *
 * Inputs are `useChannelEndpoints`' live probe: `true` / `false`, or `null`
 * when the probe has not answered. An unread probe is never rendered as either
 * answer.
 */

/** What an IM channel's status badge may claim. */
export type ImBadge = "connected" | "step1" | "disconnected" | "unknown";

export function imBadge(ep1: boolean | null, ep2: boolean | null): ImBadge {
  if (ep1 === false) return "disconnected";
  if (ep1 === true && ep2 === true) return "connected";
  if (ep1 === true && ep2 === false) return "step1";
  return "unknown";
}

/** What an IM channel's status is once ACTIVATION is known as well. */
export type ImChannelState = ImBadge | "savedNotActive";

/**
 * The COMPOSED answer, for surfaces that can also see activation — the
 * assistant card's chip and the Agent tab's notice — so both tell one story
 * from one place.
 *
 * ## Why this is a sibling and not a fifth `imBadge` state
 *
 * `imBadge` is a pure function of the GATEWAY PROBE (`channels.status` +
 * `identity.get`). `requiresRoll` comes from the lexserver REST `/details`, a
 * different source on a different cadence, and it is THREE-valued. Folding it
 * into `imBadge` would take that helper's input space from 9 pairs to 27
 * triples and force a REWRITE of the test asserting exactly one of nine pairs
 * is "connected" — the case that pins §2-bis. Composing here leaves `imBadge`,
 * its signature and its nine pinned pairs untouched.
 * (Ruled 2026-09-11 on thread `…-y94t`; the asking lane's stated preference was
 * a fifth `imBadge` state, and their actual requirement — one helper for both
 * surfaces — is what this satisfies.)
 *
 * ## The unknown is load-bearing
 *
 * `requiresRoll` follows `useChannelActivation`'s contract: `true` stored but
 * not running, `false` running, `null` UNKNOWN (unread, failed, or a runtime
 * whose `/details` omits the optional field). **Only an explicit `true` yields
 * `savedNotActive`** — an unknown renders exactly as it did before this signal
 * existed, because an older image omits the field and the SPA cannot detect
 * which fleet it is talking to.
 */
export function imChannelState(
  ep1: boolean | null,
  ep2: boolean | null,
  requiresRoll: boolean | null,
): ImChannelState {
  const badge = imBadge(ep1, ep2);
  // `imBadge` returns "disconnected" exactly when `ep1 === false`, which is
  // also what a stored-but-unactivated token looks like: no plugin polls it, so
  // the assistant's own door reads as down and the surface shows the
  // nothing-set-up state. Activation is the only thing that separates the two.
  if (badge === "disconnected" && requiresRoll === true) return "savedNotActive";
  return badge;
}

export type JourneyStepState = "done" | "current" | "todo";

/**
 * The connect stepper's three steps — ① the assistant's channel, ② you in
 * command, ✓ ready. At most ONE step is `current`, and none once both
 * endpoints are closed. A closed endpoint 2 stays `done` even while endpoint 1
 * is open again (a re-scan), exactly as the stepper always showed it.
 */
export function journeySteps(
  ep1: boolean | null,
  ep2: boolean | null,
): [JourneyStepState, JourneyStepState, JourneyStepState] {
  const step1: JourneyStepState = ep1 === true ? "done" : "current";
  const step2: JourneyStepState = ep2 === true ? "done" : ep1 === true ? "current" : "todo";
  const ready: JourneyStepState = ep1 === true && ep2 === true ? "done" : "todo";
  return [step1, step2, ready];
}
